import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import InstagramIcon from "@/components/icons/InstagramIcon";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import EmailIcon from "@/components/icons/EmailIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useStudio } from "@/contexts/StudioContext";
import {
  fetchConnectionsStatus,
  TENANT_CONNECTIONS_SCOPE,
  type ConnectionsChannelStatus,
} from "@/lib/connections-status-api";
import type { UserRole } from "@/types";
import { hasConnectionManage } from "@/lib/connection-permissions";
import ChannelConnectWizard from "./ChannelConnectWizard";
import { sanitizeReturnPath } from "./useChannelOAuthReturn";
import type { ChannelConnectType, StudioOption } from "./types";
import { connectionsStatusQueryKey } from "./connections-status-query";
import { useConnectionHealthInvalidation } from "@/hooks/useConnectionHealthInvalidation";

function showConnectCta(status: ConnectionsChannelStatus | undefined): boolean {
  return status === "disconnected" || status === "error" || status === "skipped";
}

export default function HeaderChannelConnectActions() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const { activeStudioId, isAllStudios } = useStudio();
  const [wizardChannel, setWizardChannel] = useState<ChannelConnectType | null>(null);

  const studioRole: UserRole = useMemo(() => {
    const roles = user?.studioRoles ?? [];
    if (roles.some((sr) => sr.role === "Owner")) return "Owner";
    if (roles.some((sr) => sr.role === "Manager")) return "Manager";
    if (isAllStudios || !activeStudioId) return "Staff";
    const match = roles.find((sr) => sr.studioId === activeStudioId);
    return match?.role ?? "Staff";
  }, [activeStudioId, isAllStudios, user?.studioRoles]);

  const canManage = hasConnectionManage(studioRole);
  const concreteStudioId = !isAllStudios && activeStudioId && activeStudioId !== "all" ? activeStudioId : null;

  const studioOptions: StudioOption[] = useMemo(
    () => (user?.studios ?? []).map((s) => ({ id: s.id, name: s.name })),
    [user?.studios],
  );

  useConnectionHealthInvalidation(concreteStudioId ?? undefined);

  const statusScope = concreteStudioId ?? TENANT_CONNECTIONS_SCOPE;

  const { data: statusData } = useQuery({
    queryKey: connectionsStatusQueryKey(statusScope),
    queryFn: async () => {
      const res = await fetchConnectionsStatus(statusScope);
      if (!res.success) throw new Error("Failed to load connection status");
      return res.data;
    },
    enabled: Boolean(canManage),
    refetchOnWindowFocus: true,
  });

  if (!canManage) return null;

  const returnPath = sanitizeReturnPath(location.pathname, location.search);
  const showIg = Boolean(concreteStudioId) && showConnectCta(statusData?.instagram?.status);
  const showWa = Boolean(concreteStudioId) && showConnectCta(statusData?.whatsapp?.status);
  const emailBlock = statusData?.email;
  const showEmail =
    showConnectCta(emailBlock?.status) || emailBlock?.reconnectRequired === true;

  if (!showIg && !showWa && !showEmail) return null;

  const openWizard = (channel: ChannelConnectType) => {
    if (channel !== "email" && !concreteStudioId) return;
    setWizardChannel(channel);
  };

  const disabled = isAllStudios || !concreteStudioId;

  const renderButton = (channel: ChannelConnectType, label: string, Icon: typeof InstagramIcon, emailTenantWide = false) => (
    <Tooltip key={channel}>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 text-[12px]"
            disabled={emailTenantWide ? false : disabled}
            onClick={() => openWizard(channel)}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="max-w-[140px] truncate">{label}</span>
          </Button>
        </span>
      </TooltipTrigger>
      {(emailTenantWide ? false : disabled) && (
        <TooltipContent>{t("channelConnect.header.selectStudioTooltip")}</TooltipContent>
      )}
      {emailTenantWide && (
        <TooltipContent>{t("channelConnect.email.connectTooltip")}</TooltipContent>
      )}
    </Tooltip>
  );

  const wizardStudioId = concreteStudioId ?? studioOptions[0]?.id ?? "";

  return (
    <>
      <div className="flex items-center gap-2">
        {showIg && renderButton("instagram", t("channelConnect.header.connectInstagram"), InstagramIcon)}
        {showWa && renderButton("whatsapp", t("channelConnect.header.connectWhatsapp"), WhatsAppIcon)}
        {showEmail &&
          renderButton("email", t("channelConnect.email.connect"), EmailIcon, true)}
      </div>
      {wizardChannel && wizardStudioId && (
        <ChannelConnectWizard
          channel={wizardChannel}
          open={Boolean(wizardChannel)}
          onOpenChange={(open) => {
            if (!open) setWizardChannel(null);
          }}
          studioId={wizardStudioId}
          studios={studioOptions}
          returnPath={returnPath}
        />
      )}
    </>
  );
}
